import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { KYC_LABEL, instant } from '../lib/format';
import { Badge, Field, Notice, Select, Spinner, TextInput, Toggle } from '../components/ui';
import type { KycState } from '../lib/types';
import { t } from '../lib/i18n';
import { translateServerText } from '../lib/server-text';

/**
 * Identity verification.
 *
 * What this page deliberately does not do: upload a document to Bedly. The API
 * returns a short-lived upload target that belongs to the KYC provider, and the
 * only things that come back are a reference and a verdict. There is no endpoint
 * that would accept a CNIE scan, so no future change to this screen can start
 * storing one by accident.
 */

const DOCUMENTS = [
  { value: 'CNIE', label: t("CNIE — carte nationale électronique") },
  { value: 'PASSPORT', label: t("Passeport") },
  { value: 'RESIDENCE_PERMIT', label: t("Carte de séjour") },
];

export default function Kyc() {
  const { user, refreshUser, toast, reportError } = useApp();
  const [state, setState] = useState<KycState | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [started, setStarted] = useState(false);

  const [accountType, setAccountType] = useState<'INDIVIDUAL' | 'BUSINESS'>('INDIVIDUAL');
  const [documentType, setDocumentType] = useState('CNIE');
  const [holderName, setHolderName] = useState(user?.fullName ?? '');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [nationality, setNationality] = useState('Marocaine');
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState(user?.city ?? '');
  const [iceNumber, setIceNumber] = useState('');
  const [consent, setConsent] = useState(false);
  const [session, setSession] = useState<{ url: string; expiresAt: string; id: string } | null>(null);
  const [selfieDone, setSelfieDone] = useState(false);

  // Payout account (hosts only)
  const [ribHolder, setRibHolder] = useState(user?.fullName ?? '');
  const [rib, setRib] = useState('');
  const [bankName, setBankName] = useState('');
  const [accounts, setAccounts] = useState<
    { id: string; holderName: string; ribLast4: string; status: string }[]
  >([]);

  const load = async () => {
    try {
      const response = await api.get<KycState>('/kyc/me');
      setState(response);
      if (user?.roles.includes('HOST')) {
        const accountResponse = await api
          .get<{ accounts: typeof accounts }>('/kyc/payout-accounts')
          .catch(() => ({ accounts: [] }));
        setAccounts(accountResponse.accounts);
      }
    } catch (error) {
      reportError(error);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startSession = async () => {
    setBusy(true);
    setErrors({});
    try {
      const response = await api.post<{
        submission: { id: string };
        upload: { url: string; expiresAt: string };
        manualReview: boolean;
      }>('/kyc/sessions', {
        accountType,
        documentType,
        holderName,
        ...(dateOfBirth ? { dateOfBirth } : {}),
        ...(nationality ? { nationality } : {}),
        ...(addressLine ? { addressLine } : {}),
        ...(city ? { city } : {}),
        ...(accountType === 'BUSINESS' ? { iceNumber } : {}),
        consent: true,
      });
      setSession({
        url: response.upload.url,
        expiresAt: response.upload.expiresAt,
        id: response.submission.id,
      });
      toast(t("Session de vérification ouverte."), 'ok');
    } catch (error) {
      const apiError = reportError(error);
      if (apiError instanceof ApiError) setErrors(apiError.fieldErrors());
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const response = await api.post<{ status: string; message: string | null }>(
        `/kyc/submissions/${session.id}/complete`,
        { selfieCaptured: selfieDone },
      );
      toast(response.message ? translateServerText(response.message) : t('Statut : {status}', { status: KYC_LABEL[response.status] ?? response.status }), 'ok');
      setSession(null);
      setStarted(false);
      await load();
      await refreshUser();
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const addPayoutAccount = async () => {
    setBusy(true);
    try {
      await api.post('/kyc/payout-accounts', {
        holderName: ribHolder,
        rib: rib.replace(/\s/g, ''),
        ...(bankName ? { bankName } : {}),
      });
      setRib('');
      toast(t("RIB enregistré. Seuls les 4 derniers chiffres sont conservés."), 'ok');
      await load();
    } catch (error) {
      const apiError = reportError(error);
      if (apiError instanceof ApiError) setErrors(apiError.fieldErrors());
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement…")} />
      </div>
    );
  }

  const ribDigits = rib.replace(/\D/g, '');

  return (
    <div className="page page--narrow stack stack--loose">
      <div className="stack stack--tight">
        <h1 style={{ fontSize: 26 }}>{t("Vérification d’identité")}</h1>
        <div className="row row--wrap" style={{ gap: 8 }}>
          <Badge tone={state.status === 'APPROVED' ? 'badge--verified' : 'badge--warn'}>
            {KYC_LABEL[state.status]}
          </Badge>
          <Badge>{t("Niveau")} {state.verificationLevel}/3</Badge>
          {state.emailVerified ? <Badge tone="badge--ok">{t("Email vérifié")}</Badge> : null}
        </div>
      </div>

      {state.status === 'APPROVED' ? (
        <Notice tone="ok">
          {t("Identité vérifiée. Le badge « Vérifié » apparaît sur votre profil et vos annonces.")}
        </Notice>
      ) : state.status === 'PENDING' ? (
        <Notice tone="warn">
          {t("Vérification en cours. Notre équipe répond sous 24 à 48 h et vous recevrez une notification.")}
        </Notice>
      ) : state.status === 'REJECTED' ? (
        <Notice tone="danger">
          {t("Vérification refusée.")}{' '}
          {state.submissions[0]?.rejectionReason ?? t("Reprenez les photos en lumière naturelle.")}
        </Notice>
      ) : null}

      {!started && state.status !== 'APPROVED' && state.status !== 'PENDING' ? (
        <div className="card card--pad stack">
          <h2>{t("Vérifier votre identité")}</h2>
          <p className="small muted" style={{ margin: 0 }}>
            {t("Pour la confiance des locataires comme des hôtes. Environ deux minutes.")}
          </p>
          <ul className="small" style={{ margin: 0, paddingInlineStart: 20 }}>
            <li>{t("Votre CNIE, passeport ou carte de séjour")}</li>
            <li>{t("Un selfie rapide pour confirmer que c’est bien vous")}</li>
          </ul>
          <Notice tone="info">
            {t("Vos documents sont envoyés directement au prestataire de vérification. Bedly n’en conserve que la référence et le résultat — jamais l’image.")}
          </Notice>
          <button className="btn btn--block" onClick={() => setStarted(true)}>
            {t("Commencer la vérification")}
          </button>
        </div>
      ) : null}

      {started && !session ? (
        <div className="card card--pad stack">
          <h2>{t("Vos informations")}</h2>
          <p className="small muted" style={{ margin: 0 }}>
            {t("Exactement comme sur votre pièce d’identité.")}
          </p>

          <Field label={t("Vous êtes")}>
            <Select
              value={accountType}
              onChange={(value) => setAccountType(value as 'INDIVIDUAL' | 'BUSINESS')}
              options={[
                { value: 'INDIVIDUAL', label: t("Particulier") },
                { value: 'BUSINESS', label: t("Société / agence immobilière") },
              ]}
            />
          </Field>

          <Field label={t("Document")} error={errors.documentType}>
            <Select value={documentType} onChange={setDocumentType} options={DOCUMENTS} />
          </Field>

          <Field label={t("Nom complet")} error={errors.holderName}>
            <TextInput value={holderName} onChange={setHolderName} />
          </Field>

          <div className="row row--wrap" style={{ gap: 10 }}>
            <Field label={t("Date de naissance")}>
              <input
                className="input"
                type="date"
                value={dateOfBirth}
                onChange={(event) => setDateOfBirth(event.target.value)}
              />
            </Field>
            <div style={{ flex: '1 1 140px' }}>
              <Field label={t("Nationalité")}>
                <TextInput value={nationality} onChange={setNationality} />
              </Field>
            </div>
          </div>

          <Field label={t("Adresse")}>
            <TextInput value={addressLine} onChange={setAddressLine} />
          </Field>
          <Field label={t("Ville")}>
            <TextInput value={city} onChange={setCity} />
          </Field>

          {accountType === 'BUSINESS' ? (
            <Field
              label={t("Numéro ICE (15 chiffres)")}
              error={errors.iceNumber}
              hint={t("Le Registre de Commerce sera demandé par le prestataire.")}
            >
              <TextInput
                value={iceNumber}
                onChange={(value) => setIceNumber(value.replace(/\D/g, '').slice(0, 15))}
                inputMode="numeric"
              />
            </Field>
          ) : null}

          <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span className="small">
              {t("J’autorise Bedly à traiter ces données pour vérifier mon identité, conformément à sa politique de confidentialité et à la loi 09-08 sur la protection des données personnelles.")}
            </span>
          </label>

          <div className="row" style={{ gap: 10 }}>
            <button className="btn btn--ghost" onClick={() => setStarted(false)}>
              {t("Plus tard")}
            </button>
            <button className="btn" style={{ flex: 1 }} disabled={busy || !consent} onClick={startSession}>
              {busy ? t("Ouverture…") : t("Continuer")}
            </button>
          </div>
        </div>
      ) : null}

      {session ? (
        <div className="card card--pad stack">
          <h2>{t("Envoyer vos documents")}</h2>
          <Notice tone="info">
            {t("Envoyez le recto, le verso et un selfie au prestataire via le lien ci-dessous. Ce lien expire le")} {instant(session.expiresAt)}{t(". Bedly ne voit jamais ces images.")}
          </Notice>
          <div className="card card--pad stack stack--tight">
            <span className="field__label">{t("Cible de téléversement")}</span>
            <code className="tiny" style={{ overflowWrap: 'anywhere' }}>
              {session.url}
            </code>
          </div>
          <Toggle on={selfieDone} onChange={setSelfieDone} label={t("Selfie effectué")} />
          <button className="btn btn--block" disabled={busy} onClick={complete}>
            {busy ? t("Envoi…") : t("J’ai envoyé mes documents")}
          </button>
          <p className="tiny muted" style={{ margin: 0 }}>
            {t("En configuration locale le prestataire est notre propre file de modération : un administrateur validera depuis l’espace admin.")}
          </p>
        </div>
      ) : null}

      {state.submissions.length > 0 ? (
        <div className="card card--pad stack stack--tight">
          <h3>{t("Historique")}</h3>
          {state.submissions.map((submission) => (
            <div key={submission.id} className="row row--between">
              <span className="small">
                {submission.documentType}
                {submission.documentLast4 ? ` ••••${submission.documentLast4}` : ''} ·{' '}
                {KYC_LABEL[submission.status]}
              </span>
              <span className="tiny muted">{instant(submission.createdAt)}</span>
            </div>
          ))}
        </div>
      ) : null}

      {user?.roles.includes('HOST') ? (
        <div className="card card--pad stack">
          <h2>{t("Coordonnées de versement")}</h2>
          <p className="small muted" style={{ margin: 0 }}>
            {t("Pour recevoir vos revenus d’hôte. Le RIB est transmis au prestataire de paiement et remplacé par un jeton : Bedly n’en garde que les 4 derniers chiffres.")}
          </p>
          {accounts.length > 0 ? (
            <div className="stack stack--tight">
              {accounts.map((account) => (
                <div key={account.id} className="row row--between">
                  <span className="small">
                    {account.holderName} {t("· RIB ••••")}{account.ribLast4}
                  </span>
                  <Badge tone={account.status === 'VERIFIED' ? 'badge--ok' : 'badge--warn'}>
                    {account.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : null}
          <Field label={t("Titulaire du compte")}>
            <TextInput value={ribHolder} onChange={setRibHolder} />
          </Field>
          <Field
            label={t("RIB (24 chiffres)")}
            error={errors.rib}
            hint={t('{count}/24 chiffres', { count: ribDigits.length })}
          >
            <TextInput
              value={rib}
              onChange={(value) => setRib(value.replace(/[^\d\s]/g, ''))}
              inputMode="numeric"
              placeholder="011 780 0000123456789012 34"
            />
          </Field>
          <Field label={t("Banque (optionnel)")}>
            <TextInput value={bankName} onChange={setBankName} placeholder={t("Attijariwafa Bank")} />
          </Field>
          <button
            className="btn"
            disabled={busy || ribDigits.length !== 24}
            onClick={addPayoutAccount}
          >
            {busy ? t("Enregistrement…") : t("Enregistrer le RIB")}
          </button>
        </div>
      ) : null}

      <div className="center small">
        <Link to="/compte">{t("Retour à mon compte")}</Link>
      </div>
    </div>
  );
}
